import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { paths } from "./app/paths.js";
import { useSession } from "./app/session.js";
import { AdminPage } from "./features/admin/AdminPage.js";
import { CancelRecoveryPage } from "./features/recovery/CancelRecoveryPage.js";
import { RecoveryPage } from "./features/recovery/RecoveryPage.js";
import { LoginPage } from "./features/auth/LoginPage.js";
import { DashboardPage } from "./features/dashboard/DashboardPage.js";
import { LinkExtensionPage } from "./features/extension/LinkExtensionPage.js";
import { GeneratorPage } from "./features/generator/GeneratorPage.js";
import { SettingsPage } from "./features/settings/SettingsPage.js";
import { NotFoundPage } from "./features/system/NotFoundPage.js";
import { AcceptInvitePage } from "./features/teams/AcceptInvitePage.js";
import { TeamDetailPage } from "./features/teams/TeamDetailPage.js";
import { TeamsPage } from "./features/teams/TeamsPage.js";
import { VaultPage } from "./features/vault/VaultPage.js";
import { AppShell } from "./shell/AppShell.js";
import { DeviceApprovalDialog } from "./shell/DeviceApprovalDialog.js";

function RequireVault(): ReactNode {
  const { state } = useSession();
  const location = useLocation();
  if (state.status === "locked") {
    return <Navigate to={paths.login} replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  }
  return <AppShell />;
}

export function App(): ReactNode {
  return (
    <>
      <DeviceApprovalDialog />
      <Routes>
        <Route path={paths.login} element={<LoginPage />} />
        <Route path={paths.recover} element={<RecoveryPage />} />
        <Route path={paths.cancelRecovery} element={<CancelRecoveryPage />} />
        <Route element={<RequireVault />}>
          <Route path={paths.dashboard} element={<DashboardPage />} />
          <Route path={paths.vault} element={<VaultPage />} />
          <Route path={paths.vaultNew} element={<VaultPage />} />
          <Route path="/vault/:id" element={<VaultPage />} />
          <Route path="/vault/:id/edit" element={<VaultPage />} />
          <Route path={paths.generator} element={<GeneratorPage />} />
          <Route path={paths.teams} element={<TeamsPage />} />
          <Route path="/teams/:id" element={<TeamDetailPage />} />
          <Route path={paths.invite} element={<AcceptInvitePage />} />
          <Route path={paths.settings} element={<SettingsPage />} />
          <Route path={paths.admin} element={<AdminPage />} />
          <Route path={paths.linkExtension} element={<LinkExtensionPage />} />
        </Route>
        <Route path="/" element={<Navigate to={paths.dashboard} replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
